import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import {
  ArrowRightIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/solid'
import { HandshakeIcon } from '@components/UI/Icons/handshake'
import { KeyIcon } from '@components/UI/Icons/key'

export default function HomeBuyers() {
  return (
    <>
      <NextSeo
        title="Home Repair Pricing Before Buying A House - Next Day Delivery"
        description="Get a comprehensive report of repair estimates before closing on a house. Get a home repair estimate now."
        canonical="https://majordomo.com/repair-pricing/-home-buyers"
        openGraph={{
          url: 'https://majordomo.com/repair-pricing/-home-buyers',
          title:
            'Home Repair Pricing Before Buying A House - Next Day Delivery | Majordomo',
          description:
            'Get a comprehensive report of repair estimates before closing on a house. Get a home repair estimate now.',
          images: [
            {
              url: 'https://majordomo.com/images/product-pages/buyers-hero.png',
              width: 700,
              height: 680,
              alt: 'Buyers hugging in a new home'
            },
            {
              url: 'https://majordomo.com/images/product-pages/domoreport.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s Domoreport'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <KeyIcon className="pr-2 mr-2 border-r border-gray-200 w-8 h-8 fill-current text-brand" />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Repair Pricing for Home Buyers
              </h1>
            </div>

            <p className="mb-8 xl:text-3xl lg:text-2xl text-xl font-light">
              Focus on the <span className="font-black">big picture</span>
              <br />& <span className="font-black">close on time</span>
            </p>

            <p className="md:w-full md:p-0 px-8">
              Don't get bogged down in the details: Armed with one organized
              report that assesses a home's condition and repair estimates,
              you'll move forward on a purchase with confidence.
            </p>

            <p className="md:w-full md:p-0 pt-4 px-8 md:mt-4">
              Use the Domoreport to quickly decide what you'll fix yourself,
              what you'll ask the seller to repair or whether you'll ask for a
              credit.
            </p>
          </div>

          <img
            src="/images/product-pages/buyers-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="lg:mb-36 sm:mb-12 mb-0">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 max-w-md mx-auto">
            Take action after the home inspection
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-12 md:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center lg:order-first order-last">
              <div className="flex relative">
                <ClockIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Save time after the inspection
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Our guaranteed 24-hour turnaround allows you to focus on
                    your greatest concerns and start negotiations sooner.{' '}
                    <span className="font-black">
                      No more running around or waiting for contractors to
                      return calls
                    </span>
                    .
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <HandshakeIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Negotiate smarter with sellers
                  </h4>
                  <p className="max-w-lg mr-auto">
                    The ZIP Code specific repair estimates are organized into a
                    simple and clear report that outlines project details and
                    cost estimates,{' '}
                    <span className="font-black">
                      so negotiations can be based on facts instead of feelings
                    </span>
                    . Our "Request List" tool lets you build a custom list to
                    send to the sellers. Export a custom addendum to look and be
                    professional when negotiating for concessions.{' '}
                    <Link href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list">
                      <a className="text-accentBlue hover:underline">
                        Learn more
                      </a>
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <ShieldCheckIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    An extra layer of security
                  </h4>
                  <p className="max-w-lg mr-auto">
                    The Domoreport is the most in-depth report on a home's
                    condition ever created, but it's still easy to read. The
                    report allows you to quickly figure out what defects need to
                    be addressed in a home and what it will cost to repair them.{' '}
                    <span className="font-black">Nothing is missed</span>
                  </p>
                </div>
              </div>

              <div className="flex relative self-start 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8 ml-0">
                <h4 className="max-w-lg md:text-2xl text-lg font-black tracking-tight mb-2">
                  All for just $99
                  <span className="font-light text-base"> / per report</span>
                </h4>
              </div>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16 lg:order-last order-first">
              <img
                src="/images/product-pages/domoreport.png"
                alt="Majordomo product samples"
                className="xl:max-w-xl lg:max-w-md max-w-xs"
              />
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>
        </div>

        <div className="flex lg:flex-row flex-col justify-evenly lg:pb-36 pb-12">
          <div className="bg-black lg:mt-32 mt-0 lg:w-1/2 md:p-16 p-8 text-gray-300 space-y-8">
            <h4 className="md:text-3xl text-xl font-black tracking-tight text-white">
              Majordomo Is On Your Team
            </h4>

            <div>
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-2 text-white">
                Additional resources
              </h4>

              <p>
                <b>Explore:</b> We offer a full page of online resources,
                specific to your local area, to help buyers. The information
                includes homeowner rebates, permits, maps, utilities and much
                more.{' '}
                <Link href="/features/explore-homeowner-resources">
                  <a className="text-accentBlue hover:underline">Learn more</a>
                </Link>
              </p>
            </div>

            <p>
              <b>Documents:</b> Save your home inspection report, photos and
              other important documents to Majordomo Documents and access them
              from any web browser; this service is free forever with every
              Domoreport.{' '}
              <Link href="/features/upload-property-documents">
                <a className="text-accentBlue hover:underline">Learn more</a>
              </Link>
            </p>

            <p>
              <b>Integrated Request List:</b> Build a custom request list of
              repair requests, credit amounts, and items accepted as-is. Export
              a custom addendum.{' '}
              <Link href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list">
                <a className="text-accentBlue hover:underline">Learn more</a>
              </Link>
            </p>

            <div>
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-2 text-white">
                Easy to share
              </h4>

              <p>
                Built-in tools easily allow you to share the Domoreport and
                collaborate during the inspection review period.{' '}
                <Link href="https://intercom.help/majordomo/en/articles/3216497-how-do-i-share-my-domoreport">
                  <a className="text-accentBlue hover:underline">Learn more</a>
                </Link>
              </p>
            </div>

            <div>
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-2 text-white">
                Always available
              </h4>

              <p>
                Access Majordomo on your computer, smartphone or tablet anytime.
                Have a question? Majordomo is ready to help with 24-hour chat
                and customer support.
              </p>
            </div>
          </div>

          <div className="lg:flex hidden justify-center items-center folded-l-max p-4 lg:w-1/2 w-full bg-gray-50">
            <img
              src="/images/product-pages/on-your-team.png"
              alt="A Majordomo"
              className="object-contain max-w-md"
            />
          </div>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-36 pb-12 md:max-w-3xl max-w-md mx-auto">
          Selling a home? We can help with that too.{' '}
          <Link href="/repair-pricing/home-sellers">
            <a className="text-accentBlue hover:underline">Learn More</a>
          </Link>
        </h4>
      </main>

      <Footer />
    </>
  )
}
